import React from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  AlertTitle,
} from '@mui/material';
import { Build, Code, Speed, Warning } from '@mui/icons-material';

export function PotentialSideEffectsTab() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Potential Side Effects of Migration
      </Typography>

      <Alert severity="warning" sx={{ mb: 3 }}>
        <AlertTitle>Important Notice</AlertTitle>
        While migration provides significant benefits, be aware of these potential side effects and plan accordingly.
      </Alert>

      {/* Pipeline Side Effects */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="warning.main">
            <Build sx={{ mr: 1 }} />
            Pipeline & CI/CD Side Effects
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Build Script Changes" 
                secondary="npm run build behavior may change, requiring pipeline script updates" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Environment Variables" 
                secondary="REACT_APP_ prefix no longer required in Vite, may break existing env var configurations" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Build Output Structure" 
                secondary="Output folder structure might change, affecting deployment scripts" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Docker Image Updates" 
                secondary="Dockerfile and build processes need updating for new build tools" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="warning" />
              </ListItemIcon>
              <ListItemText 
                primary="Azure Pipeline Dependencies" 
                secondary="Node.js version requirements and build steps may need adjustment" 
              />
            </ListItem>
          </List>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Pipeline Mitigation Steps:
            </Typography>
            <Typography variant="body2">
              • Update azure-pipelines.yml with new build commands
              <br />
              • Test pipeline in staging environment first
              <br />
              • Update environment variable configurations
              <br />
              • Verify deployment paths and artifact locations
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Testing Side Effects */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="error.main">
            <Code sx={{ mr: 1 }} />
            Unit Test & Integration Test Side Effects
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <Warning color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Jest Configuration Changes" 
                secondary="Test setup files and configurations may need updates for Vite" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Test Environment Setup" 
                secondary="setupTests.js and test utilities might need reconfiguration" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Mock Dependencies" 
                secondary="Static asset mocks and module mocks may break with new build system" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Test Performance" 
                secondary="Test execution time may change, affecting CI/CD pipeline timeouts" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="Coverage Reports" 
                secondary="Code coverage tools and reports may need reconfiguration" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="error" />
              </ListItemIcon>
              <ListItemText 
                primary="E2E Test Setup" 
                secondary="End-to-end tests may need updates for new development server" 
              />
            </ListItem>
          </List>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              Testing Mitigation Steps:
            </Typography>
            <Typography variant="body2">
              • Use Vitest as Jest alternative for better Vite integration
              <br />
              • Update test configurations and setup files
              <br />
              • Verify all existing tests pass after migration
              <br />
              • Update mock strategies for new module system
              <br />
              • Test coverage tools compatibility check
            </Typography>
          </Box>
        </CardContent>
      </Card>

      {/* Other Side Effects */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom color="info.main">
            <Speed sx={{ mr: 1 }} />
            Other Potential Side Effects
          </Typography>
          
          <List>
            <ListItem>
              <ListItemIcon>
                <Warning color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Legacy Browser Support" 
                secondary="Vite targets modern browsers by default, may break IE11 support" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Import Path Changes" 
                secondary="Static asset imports may need updating (images, fonts, etc.)" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Third-party Dependencies" 
                secondary="Some packages may not be compatible with Vite's ES modules approach" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Development vs Production Differences" 
                secondary="Vite uses different bundlers for dev (esbuild) and prod (Rollup)" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Hot Module Replacement" 
                secondary="HMR behavior changes may affect development workflow" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Bundle Analysis Tools" 
                secondary="Webpack-specific analysis tools won't work, need Vite alternatives" 
              />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <Warning color="info" />
              </ListItemIcon>
              <ListItemText 
                primary="Team Learning Curve" 
                secondary="Developers need to learn new tooling and configuration patterns" 
              />
            </ListItem>
          </List>

          <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>
              General Mitigation Steps:
            </Typography>
            <Typography variant="body2">
              • Plan for adequate testing time in staging environment
              <br />
              • Prepare team training on new tools and workflows
              <br />
              • Keep CRA version as backup until migration is fully validated
              <br />
              • Update documentation and development guides
              <br />
              • Monitor application performance after deployment
            </Typography>
          </Box>
        </CardContent>
      </Card>

      <Alert severity="success">
        <AlertTitle>Migration Success Tips</AlertTitle>
        Most side effects can be mitigated with proper planning and testing. The benefits of modern tooling 
        typically outweigh the temporary migration challenges. Start with a non-critical project to gain 
        experience before migrating mission-critical applications.
      </Alert>
    </Box>
  );
}
